// US-28/US-29 — Proyectos (SpecBox Cloud + HQ enrichment + rendimiento)
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { AlertCircle, FolderKanban, Search, X, Flame } from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  EmptyState,
  StatusPill,
  type StatusPillProps,
} from "@/components/ui/v2";
import { Amount } from "@/components/masked/Amount";
import { useProjects } from "@/hooks/useProjects";
import { useWeeklyCapacity } from "@/hooks/useWeeklyCapacity";
import { ProjectDetailSheet } from "@/components/projects/ProjectDetailSheet";
import { WorkloadChart } from "@/components/projects/WorkloadChart";
import { ThroughputSparkline } from "@/components/projects/ThroughputSparkline";
import { InfoHint } from "@/components/ui/InfoHint";
import {
  COMMERCIAL_STATUS_LABEL,
  PROJECT_KIND_LABEL,
  projectColor,
  type Project,
  type ProjectCommercialStatus,
  type ProjectKind,
} from "@/types/projects";
import {
  hasNoSpecs,
  progressPct,
  isHot,
  parseMonetary,
  aggregateActiveHours,
  isStalled,
  formatLeadTime,
  aggregateEngineLeadTime,
} from "@/domain/projects/projectMetrics";
import { formatRelative, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const STATUS_OPTIONS: ProjectCommercialStatus[] = [
  "active",
  "lead",
  "paused",
  "closed",
  "cancelled",
];

const STATUS_TO_PILL: Record<ProjectCommercialStatus, StatusPillProps["status"]> = {
  lead: "neutral",
  active: "paid",
  paused: "pending",
  closed: "neutral",
  cancelled: "overdue",
};

function KindChip({ kind }: { kind: ProjectKind }) {
  return (
    <span
      className={cn(
        "inline-flex items-center h-5 px-2 rounded-sm text-xs font-medium",
        kind === "client" ? "bg-accent-soft text-accent" : "bg-subtle text-fg-secondary",
      )}
    >
      {PROJECT_KIND_LABEL[kind]}
    </span>
  );
}

function NameCell({ project }: { project: Project }) {
  const hot = isHot(project);
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span
        className="inline-block size-2 rounded-full shrink-0"
        style={{ backgroundColor: projectColor(project) }}
        aria-hidden
      />
      <span className="font-medium text-fg truncate">{project.name}</span>
      {hot ? (
        <span title="Actividad alta" className="inline-flex shrink-0">
          <Flame className="size-3.5 text-warning" strokeWidth={1.5} aria-label="Actividad alta" />
        </span>
      ) : null}
    </div>
  );
}

function ProgressCell({ project }: { project: Project }) {
  const prog = project.progress;
  const noSpecs = hasNoSpecs(prog);
  if (noSpecs) {
    return <span className="text-xs text-fg-tertiary italic">sin specs</span>;
  }
  const pct = progressPct(prog);
  return (
    <div className="flex flex-col gap-1 w-full min-w-[80px]">
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono tabular-nums text-xs text-fg">{pct.toFixed(1)}%</span>
        <span className="text-[11px] tabular-nums text-fg-tertiary">
          {prog.ac_done}/{prog.ac_total}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-subtle overflow-hidden">
        <div
          className="h-full rounded-full bg-accent"
          style={{ width: `${Math.min(100, pct)}%` }}
          aria-hidden
        />
      </div>
    </div>
  );
}

function LastActivityCell({ project }: { project: Project }) {
  const at = project.activity.last_activity_at;
  if (!at) return <span className="text-fg-tertiary tabular-nums">—</span>;
  return (
    <span className="tabular-nums text-fg-secondary text-xs" title={formatDate(at, "long")}>
      {formatRelative(at)}
    </span>
  );
}

function MoneyCell({ value }: { value: string | null }) {
  if (!value) return <span className="text-fg-tertiary">—</span>;
  const n = parseMonetary(value);
  if (n === null) return <span className="text-fg-tertiary">—</span>;
  return <Amount value={n} />;
}

/** US-29 — Columna "Ritmo": sparkline 8 semanas + lead time p50 + indicador estancado */
function RitmoCell({ project }: { project: Project }) {
  const perf = project.performance;
  if (!perf) {
    return <span className="text-fg-tertiary text-xs">—</span>;
  }
  const stalled = isStalled(perf);
  return (
    <div className="flex items-center gap-2 min-w-0">
      <ThroughputSparkline points={perf.weekly_throughput} />
      <span className="font-mono tabular-nums text-xs text-fg-secondary shrink-0">
        {formatLeadTime(perf.lead_time_p50_seconds)}
      </span>
      {stalled ? (
        <span
          className="inline-block size-2 rounded-full bg-warning shrink-0"
          title="Sin UC completados en 2 semanas"
          aria-label="Proyecto estancado: sin UC completados en 2 semanas"
        />
      ) : null}
    </div>
  );
}

interface Filters {
  status: ProjectCommercialStatus[];
  kind: ProjectKind | "";
  search: string;
  includeClosed: boolean;
}

function applyFilters(items: Project[], filters: Filters): Project[] {
  return items.filter((p) => {
    if (
      filters.status.length > 0 &&
      (p.commercial_status === null || !filters.status.includes(p.commercial_status))
    )
      return false;
    if (!filters.includeClosed && !filters.status.length) {
      if (p.commercial_status === "closed" || p.commercial_status === "cancelled") return false;
    }
    if (filters.kind && p.kind !== filters.kind) return false;
    if (filters.search.trim()) {
      const q = filters.search.trim().toLowerCase();
      if (!p.name.toLowerCase().includes(q)) return false;
    }
    return true;
  });
}

function ProjectsPage() {
  const [statusFilter, setStatusFilter] = useState<ProjectCommercialStatus[]>([]);
  const [kindFilter, setKindFilter] = useState<ProjectKind | "">("");
  const [search, setSearch] = useState("");
  const [includeClosed, setIncludeClosed] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selected, setSelected] = useState<Project | null>(null);

  const { items, isLoading, error } = useProjects();
  const { capacity } = useWeeklyCapacity();

  const filters: Filters = useMemo(
    () => ({ status: statusFilter, kind: kindFilter, search, includeClosed }),
    [statusFilter, kindFilter, search, includeClosed],
  );

  const filtered = useMemo(() => applyFilters(items, filters), [items, filters]);

  const activeProjects = useMemo(
    () => items.filter((p) => p.commercial_status === "active"),
    [items],
  );

  // AC-15: aggregated active hours from domain helper
  const activeHours = useMemo(() => aggregateActiveHours(items), [items]);

  const hasAnyFilter =
    statusFilter.length > 0 || kindFilter !== "" || search !== "" || includeClosed;

  function clearFilters() {
    setStatusFilter([]);
    setKindFilter("");
    setSearch("");
    setIncludeClosed(false);
  }

  function toggleStatus(s: ProjectCommercialStatus) {
    setStatusFilter((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  function openProject(project: Project) {
    setSelected(project);
    setSheetOpen(true);
  }

  // Table column definitions (no drag-and-drop — order from backend)
  // US-29: added "Ritmo" column (sparkline + lead time p50 + stalled indicator)
  const colTemplate =
    "40px minmax(180px,2fr) 80px 100px 160px 80px 100px 100px minmax(120px,1fr) minmax(140px,1fr)";

  // US-29: aggregate engine lead time from all (unfiltered) items
  const engineLeadTime = useMemo(() => aggregateEngineLeadTime(items), [items]);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-lg font-semibold tracking-tight text-fg">Proyectos</h1>
          <p className="text-sm text-fg-secondary">
            Proyectos de SpecBox Cloud + enriquecimiento HQ. Carga activa:{" "}
            <span className="font-mono tabular-nums text-fg">{activeHours.toFixed(1)}h</span>
            <span className="text-fg-tertiary"> / {capacity}h sem</span>
          </p>
          {/* US-29: KPI agregado del motor */}
          <p className="text-xs text-fg-tertiary inline-flex items-center gap-1 flex-wrap">
            <span className="inline-flex items-center gap-1">
              Lead time del motor
              <InfoHint id="perf.engine_lead_time" />:
            </span>{" "}
            {engineLeadTime ? (
              <>
                <span className="font-mono tabular-nums text-fg-secondary">
                  p50 {formatLeadTime(engineLeadTime.p50)} · p90{" "}
                  {formatLeadTime(engineLeadTime.p90)}
                </span>
                <span className="text-fg-tertiary"> ({engineLeadTime.sampleCount} UC medidos)</span>
              </>
            ) : (
              <span className="text-fg-tertiary italic">sin datos suficientes</span>
            )}
          </p>
        </div>
      </header>

      {/* Workload chart (AC-15) */}
      <Card padding="md">
        <CardHeader>
          <div>
            <CardTitle>Carga semanal activa</CardTitle>
            <CardDescription>
              Suma de horas/semana de los proyectos con estado Activo
            </CardDescription>
          </div>
        </CardHeader>
        {activeProjects.length === 0 ? (
          <EmptyState
            padding="sm"
            icon={FolderKanban}
            title="Sin proyectos activos"
            description="Los proyectos con estado Activo aparecen aquí con su carga horaria."
          />
        ) : (
          <WorkloadChart projects={activeProjects} capacity={capacity} />
        )}
      </Card>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_OPTIONS.map((s) => {
              const selected = statusFilter.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatus(s)}
                  className={cn(
                    "inline-flex items-center h-7 px-2.5 rounded-sm text-xs font-medium border transition-colors duration-fast",
                    selected
                      ? "bg-accent-soft border-accent/30 text-accent"
                      : "bg-surface border-default text-fg-secondary hover:bg-subtle",
                  )}
                >
                  {COMMERCIAL_STATUS_LABEL[s]}
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-fg-tertiary"
                strokeWidth={1.5}
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre…"
                className="w-full h-9 pl-9 pr-3 rounded-md text-sm bg-surface border border-default text-fg placeholder:text-fg-tertiary focus:outline-none focus:ring-2 focus:ring-accent/40"
              />
            </div>
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value as ProjectKind | "")}
              className="h-9 px-3 rounded-md text-sm bg-surface border border-default text-fg focus:outline-none focus:ring-2 focus:ring-accent/40"
            >
              <option value="">Todos los tipos</option>
              <option value="client">Cliente</option>
              <option value="own">Propio</option>
            </select>
            <label className="flex items-center gap-2 h-9 px-3 rounded-md text-sm text-fg-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={includeClosed}
                onChange={(e) => setIncludeClosed(e.target.checked)}
                className="size-4 rounded border-strong bg-surface accent-accent"
              />
              Incluir cerrados/cancelados
            </label>
          </div>
          {hasAnyFilter ? (
            <button
              type="button"
              onClick={clearFilters}
              className="self-start inline-flex items-center gap-1.5 text-xs text-fg-secondary hover:text-fg transition-colors"
            >
              <X className="size-3" strokeWidth={1.5} />
              Limpiar filtros
            </button>
          ) : null}
        </div>
      </Card>

      {/* Table (AC-01/02/04/05/07/08/12/13/16) */}
      <Card padding="md">
        {error ? (
          <EmptyState
            padding="lg"
            icon={AlertCircle}
            title="No se pudo cargar desde SpecBox Cloud"
            description={`Error al conectar con el backend: ${error.message}. Comprueba la conexión e intenta de nuevo.`}
          />
        ) : isLoading ? (
          <ul className="flex flex-col">
            {Array.from({ length: 5 }).map((_, i) => (
              <li
                key={i}
                className="h-10 border-b border-default last:border-b-0 bg-subtle animate-pulse"
              />
            ))}
          </ul>
        ) : filtered.length === 0 ? (
          <EmptyState
            padding="lg"
            icon={FolderKanban}
            title="Sin proyectos"
            description={
              hasAnyFilter
                ? "No hay proyectos que coincidan con los filtros actuales."
                : "Los proyectos aparecen aquí cuando se crean en SpecBox Cloud."
            }
            cta={hasAnyFilter ? { label: "Limpiar filtros", onClick: clearFilters } : undefined}
          />
        ) : (
          <div role="table" aria-label="Proyectos" className="w-full text-sm">
            {/* Header */}
            <div
              role="row"
              className="grid border-b border-default text-xs uppercase text-fg-secondary"
              style={{ gridTemplateColumns: colTemplate }}
            >
              <div role="columnheader" aria-hidden />
              <div role="columnheader" className="px-4 py-2">
                Nombre
              </div>
              <div role="columnheader" className="px-4 py-2">
                Tipo
              </div>
              <div role="columnheader" className="px-4 py-2">
                Estado
              </div>
              <div role="columnheader" className="px-4 py-2">
                Progreso AC
              </div>
              <div role="columnheader" className="px-4 py-2 text-right">
                UC
              </div>
              <div role="columnheader" className="px-4 py-2 text-right">
                Precio
              </div>
              <div role="columnheader" className="px-4 py-2 text-right">
                Cobrado
              </div>
              <div role="columnheader" className="px-4 py-2">
                Últ. actividad
              </div>
              <div role="columnheader" className="px-4 py-2">
                Ritmo
              </div>
            </div>
            {/* Rows */}
            <div role="rowgroup">
              {filtered.map((project) => (
                <div
                  key={project.project_id}
                  role="row"
                  className="grid w-full border-b border-default transition-colors duration-fast hover:bg-subtle cursor-pointer group"
                  style={{ gridTemplateColumns: colTemplate }}
                  onClick={() => openProject(project)}
                >
                  {/* color dot column */}
                  <div role="cell" className="flex items-center justify-center">
                    <span
                      className="inline-block size-2 rounded-full"
                      style={{ backgroundColor: projectColor(project) }}
                      aria-hidden
                    />
                  </div>
                  <div role="cell" className="px-4 py-2.5 flex items-center min-h-10">
                    <NameCell project={project} />
                  </div>
                  <div role="cell" className="px-4 py-2.5 flex items-center">
                    <KindChip kind={project.kind ?? "client"} />
                  </div>
                  <div role="cell" className="px-4 py-2.5 flex items-center">
                    <StatusPill
                      status={STATUS_TO_PILL[project.commercial_status ?? "active"]}
                      label={COMMERCIAL_STATUS_LABEL[project.commercial_status ?? "active"]}
                    />
                  </div>
                  <div role="cell" className="px-4 py-2.5 flex items-center">
                    <ProgressCell project={project} />
                  </div>
                  <div role="cell" className="px-4 py-2.5 flex items-center justify-end">
                    <span className="font-mono tabular-nums text-xs text-fg-secondary">
                      {project.progress.uc_total}
                    </span>
                  </div>
                  <div role="cell" className="px-4 py-2.5 flex items-center justify-end">
                    <MoneyCell value={project.price_agreed_eur} />
                  </div>
                  <div role="cell" className="px-4 py-2.5 flex items-center justify-end">
                    <MoneyCell value={project.amount_charged_eur} />
                  </div>
                  <div role="cell" className="px-4 py-2.5 flex items-center">
                    <LastActivityCell project={project} />
                  </div>
                  <div role="cell" className="px-4 py-2.5 flex items-center">
                    <RitmoCell project={project} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <ProjectDetailSheet
        open={sheetOpen}
        onClose={() => {
          setSheetOpen(false);
          setSelected(null);
        }}
        project={selected}
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/projects")({
  component: ProjectsPage,
});
